// ════════════════════════════════════════════════════════════════════
// TERROR — H5 · FACTION WARS
// ────────────────────────────────────────────────────────────────────
// "Weekly faction-vs-faction win tally, Plaza banner shows the leader,
//  winners get a title + backdrop tint for the week."  — the roadmap
//
// ── THE SHAPE ────────────────────────────────────────────────────────
//   games/terror/factionWars/
//     {weekKey}/
//       tally/{factionId}       : number          ← every Solrend won
//       counted/{uid}           : number          ← that player's share
//       settled                 : { at, winner, margin } | absent//
// ⚠ IT LIVES IN THE DRAWER, AND IT DID NOT USED TO. This shipped on a
// new TOP-LEVEL node `factionWars/` and a wrong belief that
// IE_FIREBASE_LAW §1's new-field exemption covered it. §1 is about new
// fields under profiles/{uid} — which genuinely have no $extra guard —
// and says nothing about top-level nodes, which §2 forbids outright and
// which rules.json denies with a
// `"$other": { ".read": false, ".write": false }` catch-all.
//
// So every tally increment and every read here would have failed
// permission_denied on the live database — SILENTLY, because this layer
// is fire-and-forget by contract. The banner would have read "nothing
// won yet this week" forever and nothing would have looked broken.
// Every test passed because no test talks to Firebase.
//
// Moved under games/terror, where the $gameId wildcard already grants
// authed read+write. Zero rules deploy — the law's own drawer was the
// whole fix. (profiles/{uid}/factionWars, the per-player spoils stamp,
// was always fine: that IS a §1 profile field.)
//
// A week is a NODE, not a rollover. Nothing is ever reset, nothing is
// ever deleted, and last week's board is readable forever — which means
// "who won week 34" is a read and not an archaeology dig.
//
// ── THIS FILE IS A LEAF, ON PURPOSE ──────────────────────────────────
// It imports firebase and nothing else. Not progression, not
// FactionHall, not the theme. That is what lets progression.js call
// tallyFactionWin() from inside the win path without a circular import,
// and it is why the WINNER'S SPOILS are granted by the app layer
// (FactionWarBanner) rather than in here: this file decides WHO won and
// records that it is settled; paying is somebody else's import.
//
// The faction NAMES, colours and dialogue stay in FactionHall.js, which
// is their one source of truth. All this file knows is three ids and
// the order they are listed in. auditFactions() proves the two agree
// and lives in the banner, which is already importing both.
//
// ── THERE IS NO SERVER, AND THE DESIGN ADMITS IT ─────────────────────
// A week settles when a CLIENT notices it is over — the first player to
// open the Plaza after Monday 00:00 UTC settles the week before. The
// settlement write is a transaction that aborts if somebody already did
// it, so ten clients noticing at once produce one settlement. A week
// nobody opens stays unsettled until somebody does, and that is fine:
// the tally is already permanent, so settling late changes nothing but
// the timestamp.
//
// ── WHAT COUNTS ──────────────────────────────────────────────────────
// Every Solrend a faction member WINS: story, free play, and ranked.
// Those are the three sites that call mirrorLeaderboard(), which is the
// one place in the codebase where "a win just landed" is already true,
// so H5 rides it instead of minting a fourth win path.
// Endless Depths wins are DELIBERATELY out — the Depths run their own
// ELO ladder and their win counts are an order of magnitude larger; one
// Depths grinder would decide the war every week. Reversible: it is one
// call in recordDepthResult if caP'n wants the Depths in.
// ════════════════════════════════════════════════════════════════════

import { ref, get, onValue, runTransaction, update } from "firebase/database";
import { db, GAME_BASE } from "./firebase";

// The three, in banner order. FactionHall owns everything else about
// them; this is the roster and nothing more.
// -- WHO A PLAYER FIGHTS FOR ---------------------------------------
// Nullus and Omnis are stored as `profile.faction`. The Jesters are a
// BOOLEAN (`isJester`), because you do not pledge to J'ynxi -- you are
// simply already one of hers. Every consumer that wants "which of the
// three" must ask through here, or the Jesters silently score zero
// forever, which is exactly what happened until 2026-08-19.
export const factionOf = (p) => (p?.faction || (p?.isJester ? "jesters" : null));

export const WAR_FACTIONS = ["nullus", "omnis", "jesters"];

// ── THE WEEK ──────────────────────────────────────────────────────────
// UTC, Monday-start, ISO-8601 week numbering. UTC and not local because
// a war whose week boundary moves with the player's timezone is a war
// with three different scoreboards.
export function weekKeyFor(ts = Date.now()) {
  const d = new Date(ts);
  // Shift to the Thursday of this week: ISO weeks are numbered by the
  // week containing the year's first Thursday, and anchoring on Thursday
  // is what makes the year rollover correct without a special case.
  const day = (d.getUTCDay() + 6) % 7; // Mon=0 … Sun=6
  const thu = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() - day + 3));
  const firstThu = new Date(Date.UTC(thu.getUTCFullYear(), 0, 4));
  const firstDay = (firstThu.getUTCDay() + 6) % 7;
  firstThu.setUTCDate(firstThu.getUTCDate() - firstDay + 3);
  const week = 1 + Math.round((thu - firstThu) / (7 * 86400000));
  return `${thu.getUTCFullYear()}-W${String(week).padStart(2, "0")}`;
}

// Millisecond boundaries of the week a key names — used for the
// countdown, and for deciding whether a week is over.
export function weekBounds(key = weekKeyFor()) {
  const m = /^(\d{4})-W(\d{2})$/.exec(key);
  if (!m) return null;
  const year = +m[1], week = +m[2];
  const firstThu = new Date(Date.UTC(year, 0, 4));
  const firstDay = (firstThu.getUTCDay() + 6) % 7;
  const week1Mon = new Date(Date.UTC(year, 0, 4 - firstDay));
  const start = week1Mon.getTime() + (week - 1) * 7 * 86400000;
  return { start, end: start + 7 * 86400000 };
}

export const currentWeekKey = () => weekKeyFor();

export const previousWeekKey = (key = weekKeyFor()) => {
  const b = weekBounds(key);
  return b ? weekKeyFor(b.start - 1) : null;
};

// How long is left in the week, in ms. Negative means the week is over
// and has not been settled yet.
export const msLeftIn = (key = weekKeyFor(), now = Date.now()) => {
  const b = weekBounds(key);
  return b ? b.end - now : 0;
};

// ── THE TALLY WRITE ───────────────────────────────────────────────────
// Called from progression's mirrorLeaderboard — the single point where
// a Terror win is already recorded. Two atomic increments, both
// transactions, both allowed to fail without touching the win.
//
// Fire-and-forget by contract: this NEVER throws into the win path. A
// war tally that fails is a war tally that is one short. A win that
// fails because of a war tally is a bug report.
export async function tallyFactionWin(uid, factionId) {
  if (!uid || !WAR_FACTIONS.includes(factionId)) return { ok: false, reason: "bad-args" };
  const week = currentWeekKey();
  try {
    await runTransaction(ref(db, `${GAME_BASE}/factionWars/${week}/tally/${factionId}`), (n) => (n || 0) + 1);
    await runTransaction(ref(db, `${GAME_BASE}/factionWars/${week}/counted/${uid}`), (n) => (n || 0) + 1);
    return { ok: true, week, factionId };
  } catch (e) {
    console.warn("[factionWars] tally failed (non-fatal):", e?.message);
    return { ok: false, reason: "write-failed" };
  }
}

// ── THE READ ──────────────────────────────────────────────────────────

export const readWeek = async (key = currentWeekKey()) => {
  try {
    const snap = await get(ref(db, `${GAME_BASE}/factionWars/${key}`));
    return snap.val() || {};
  } catch (e) {
    console.warn("[factionWars] read failed:", e?.message);
    return {};
  }
};

// Live view of one week. Returns the unsubscribe, always a function.
export const listenToWeek = (key, cb) => {
  if (!key || typeof cb !== "function") return () => {};
  return onValue(
    ref(db, `${GAME_BASE}/factionWars/${key}`),
    (snap) => cb(snap.val() || {}),
    () => cb({})
  );
};

// ── THE LEADER ────────────────────────────────────────────────────────
// A TIE IS A REAL RESULT AND IT IS NOT A WIN. Returning the
// alphabetically-first faction on a tie would hand a title to whoever
// happened to sort first, forever, invisibly. `winner: null` with
// `tied: [...]` is the honest answer and the banner can say "dead heat"
// instead of lying.
export function standingsOf(week = {}) {
  const tally = week.tally || {};
  const rows = WAR_FACTIONS
    .map((id) => ({ id, wins: tally[id] || 0 }))
    .sort((a, b) => b.wins - a.wins);
  const total = rows.reduce((s, r) => s + r.wins, 0);
  const top = rows[0]?.wins || 0;
  const tied = rows.filter((r) => r.wins === top && top > 0).map((r) => r.id);
  return {
    rows: rows.map((r) => ({ ...r, share: total ? r.wins / total : 0 })),
    total,
    winner: tied.length === 1 ? tied[0] : null,
    tied: tied.length > 1 ? tied : [],
    margin: top - (rows[1]?.wins || 0),
    empty: total === 0,
  };
}

// Did this player put anything into this week's pot? Used to gate the
// spoils: a faction wins the week, but only members who FOUGHT wear it.
export const contributedTo = (week = {}, uid) => !!(uid && (week.counted || {})[uid]);

// ── THE SETTLEMENT ────────────────────────────────────────────────────
// Marks a finished week settled and returns what the app layer owes.
// Transaction-guarded: the first caller wins, everyone else gets
// { already: true } and pays nothing twice.
//
// Returns:
//   { ok:false, reason }                       — not over / no winner / raced
//   { ok:true,  already:true, settled }        — somebody settled it first
//   { ok:true,  winner, margin, week }         — YOU settled it; pay the spoils
export async function settleWeek(key) {
  if (!key) return { ok: false, reason: "bad-args" };
  const b = weekBounds(key);
  if (!b) return { ok: false, reason: "bad-key" };
  if (Date.now() < b.end) return { ok: false, reason: "not-over" };

  const week = await readWeek(key);
  const st = standingsOf(week);
  if (st.empty) return { ok: false, reason: "no-wins" };
  if (!st.winner) return { ok: false, reason: "tied" };

  const stamp = { at: Date.now(), winner: st.winner, margin: st.margin };
  const res = await runTransaction(
    ref(db, `${GAME_BASE}/factionWars/${key}/settled`),
    (current) => current || stamp
  );
  const landed = res?.snapshot?.val() || stamp;
  const mine = landed.at === stamp.at;
  return mine
    ? { ok: true, week: key, winner: st.winner, margin: st.margin }
    : { ok: true, already: true, settled: landed, week: key };
}

// ── THE SPOILS, as data ───────────────────────────────────────────────
// What a week's winner is owed. The app layer reads this and pays it;
// nothing here writes a cosmetic, because that would mean importing
// progression and putting a cycle through the win path.
//
// The TINT is not stored anywhere. It is derived from the settled week
// every time it is asked for, which means it expires by itself the
// moment the next week settles — no expiry job, no stale flag, nothing
// to clean up. A cosmetic that turns itself off is worth more than a
// cosmetic with a cron behind it.
export const warTitleIdFor = (factionId) => `title_war_${factionId}`;

// The banner reads this to decide whether a player wears the tint
// RIGHT NOW: their faction won the most recent settled week and they
// were one of the people who did it.
export const warTintClaim = ({ settled, week, uid, faction }) => {
  if (!settled || !settled.winner) return { active: false };
  if (faction !== settled.winner) return { active: false };
  if (!contributedTo(week || {}, uid)) return { active: false, reason: "did-not-fight" };
  return { active: true, faction: settled.winner, since: settled.at };
};

// Record the spoils on the player so a future session can see WHY they
// hold the title, without re-deriving a week that has since scrolled
// away. Append-only under the week key; never overwritten.
export async function stampSpoils(uid, key, factionId) {
  if (!uid || !key || !factionId) return false;
  try {
    await update(ref(db, `profiles/${uid}/factionWars/${key}`), {
      faction: factionId, at: Date.now(),
    });
    return true;
  } catch (e) {
    console.warn("[factionWars] spoils stamp failed (non-fatal):", e?.message);
    return false;
  }
}

const factionWars = {
  WAR_FACTIONS, weekKeyFor, weekBounds, currentWeekKey, previousWeekKey,
  msLeftIn, tallyFactionWin, readWeek, listenToWeek, standingsOf,
  contributedTo, settleWeek, warTitleIdFor, warTintClaim, stampSpoils,
};
export default factionWars;
